import { useDispatch } from 'react-redux';
import { callAIModel } from '../service/api.v2.service';
import { updateAIProcessedPhoto } from '../store/photoSlice';
import { store } from '../store/store';
import { convertFileToBase64 } from '../utils/fileUtils';

type AIProcessType = 'skinImprovement' | 'characterize';

export const useAIProcessing = () => {
  const dispatch = useDispatch();

  const processWithoutAI = async (
    file: File,
    index: number,
    type: AIProcessType
  ): Promise<void> => {
    const requestSessionId = store.getState().photo.sessionId;

    try {
      const photo = await convertFileToBase64(file);

      const currentSessionId = store.getState().photo.sessionId;
      if (requestSessionId !== currentSessionId) {
        console.warn(`세션이 변경되어 ${type} 원본 처리 결과 무시 (요청: ${requestSessionId}, 현재: ${currentSessionId})`);
        return;
      }

      if (index >= 0) {
        dispatch(updateAIProcessedPhoto({ index, type, photo }));
      }
    } catch (error) {
      console.error(`${type} 원본 처리 중 오류 발생:`, error);
    }
  };

  const processWithAI = async (
    file: File,
    modelCode: string,
    blockId: number,
    index: number,
    type: AIProcessType
  ): Promise<void> => {
    const requestSessionId = store.getState().photo.sessionId;

    try {
      const processedPhoto = await callAIModel(file, modelCode, blockId);

      const currentSessionId = store.getState().photo.sessionId;
      if (requestSessionId !== currentSessionId) {
        console.warn(`세션이 변경되어 ${type} AI 처리 결과 무시 (요청: ${requestSessionId}, 현재: ${currentSessionId})`);
        return;
      }

      if (index >= 0) {
        dispatch(
          updateAIProcessedPhoto({
            index,
            type,
            photo: processedPhoto,
          })
        );
      }
    } catch (error) {
      console.error(`${type} AI 처리 중 오류 발생:`, error);
      await processWithoutAI(file, index, type);
    }
  };

  const processSkinImprovement = async (
    file: File,
    index: number,
    useAI: boolean,
    skinModelCode: string,
    blockId: number
  ): Promise<void> => {
    if (useAI) {
      await processWithAI(file, skinModelCode, blockId, index, 'skinImprovement');
    } else {
      await processWithoutAI(file, index, 'skinImprovement');
    }
  };

  const processCharacterize = async (
    file: File,
    index: number,
    useAI: boolean,
    characterizeModelCode: string,
    blockId: number
  ): Promise<void> => {
    if (useAI) {
      await processWithAI(file, characterizeModelCode, blockId, index, 'characterize');
    } else {
      await processWithoutAI(file, index, 'characterize');
    }
  };

  return {
    processSkinImprovement,
    processCharacterize,
    processWithoutAI,
    processWithAI,
  };
};
